import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/useToast';
import {
    Bell,
    Building,
    Globe,
    Lock,
    Moon,
    Palette,
    Save,
    Settings,
    Shield,
    Sun,
    User,
    Users
} from 'lucide-react';
import { useState } from 'react';

const SettingsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  
  // Theme state
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    }
    return 'light';
  });

  // Notification preferences
  const [notifications, setNotifications] = useState({
    email: true,
    browser: true,
    awbUpdates: true,
    manifestUpdates: true,
    systemAlerts: true,
  });
  const notificationOptions: Array<{
    key: keyof typeof notifications;
    label: string;
    description: string;
  }> = [
    {
      key: 'email',
      label: 'Email Notifications',
      description: 'Receive notifications via email',
    },
    {
      key: 'browser',
      label: 'Browser Notifications',
      description: 'Show desktop notifications in browser',
    },
    {
      key: 'awbUpdates',
      label: 'AWB Updates',
      description: 'Get notified about AWB status changes',
    },
    {
      key: 'manifestUpdates',
      label: 'Manifest Updates',
      description: 'Get notified about manifest changes',
    },
    {
      key: 'systemAlerts',
      label: 'System Alerts',
      description: 'Important system announcements and alerts',
    },
  ];

  // Check if user is admin
  const isAdmin = user?.role === 'SUPERADMIN' || user?.role === 'MANAGER' || user?.role === 'DIRECTOR';

  const handleThemeToggle = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
    
    toast({
      title: 'Theme Updated',
      description: `Switched to ${newTheme} mode`,
    });
  };

  const handleNotificationChange = (key: keyof typeof notifications) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
    
    toast({
      title: 'Notification Preference Updated',
      description: 'Your notification settings have been saved.',
    });
  };

  const handleSaveSettings = () => {
    toast({
      title: 'Settings Saved',
      description: 'Your settings have been saved successfully.',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground">
            Manage your application preferences and configuration
          </p>
        </div>
        <Button onClick={handleSaveSettings} className="w-full sm:w-auto">
          <Save className="h-4 w-4 mr-2" />
          Save Changes
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid h-auto w-full grid-cols-2 gap-2 bg-muted/80 p-2 sm:max-w-[640px] sm:grid-cols-4 sm:gap-1">
          <TabsTrigger value="general" className="flex items-center gap-2 whitespace-normal py-2 text-xs sm:whitespace-nowrap sm:text-sm">
            <Settings className="h-4 w-4 shrink-0" />
            General
          </TabsTrigger>
          <TabsTrigger value="appearance" className="flex items-center gap-2 whitespace-normal py-2 text-xs sm:whitespace-nowrap sm:text-sm">
            <Palette className="h-4 w-4 shrink-0" />
            Appearance
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2 whitespace-normal py-2 text-xs sm:whitespace-nowrap sm:text-sm">
            <Bell className="h-4 w-4 shrink-0" />
            Notifications
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="admin" className="flex items-center gap-2 whitespace-normal py-2 text-xs sm:whitespace-nowrap sm:text-sm">
              <Shield className="h-4 w-4 shrink-0" />
              Admin
            </TabsTrigger>
          )}
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                <CardTitle>Profile Information</CardTitle>
              </div>
              <CardDescription>
                Your account details and profile settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input id="name" defaultValue={user?.name || ''} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input id="email" type="email" defaultValue={user?.email || ''} disabled />
                  <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{user?.role}</Badge>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Account Status</Label>
                  <div className="flex items-center gap-2">
                    <Badge variant={user?.status === 'ACTIVE' ? 'default' : 'destructive'}>
                      {user?.status}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-primary" />
                <CardTitle>Security</CardTitle>
              </div>
              <CardDescription>
                Manage your password and security settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current Password</Label>
                  <Input id="current-password" type="password" placeholder="••••••••" />
                </div>
                <div className="hidden md:block"></div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input id="new-password" type="password" placeholder="••••••••" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input id="confirm-password" type="password" placeholder="••••••••" />
                </div>
              </div>
              <Button variant="outline" className="mt-4 w-full sm:w-auto">
                Update Password
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Appearance Settings */}
        <TabsContent value="appearance" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Palette className="h-5 w-5 text-primary" />
                <CardTitle>Theme</CardTitle>
              </div>
              <CardDescription>
                Customize the appearance of the application
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-0.5">
                  <Label>Dark Mode</Label>
                  <p className="text-sm text-muted-foreground">
                    Switch between light and dark themes
                  </p>
                </div>
                <div className="flex items-center gap-2 sm:justify-end">
                  <Sun className="h-4 w-4 text-muted-foreground" />
                  <Switch
                    checked={theme === 'dark'}
                    onCheckedChange={handleThemeToggle}
                  />
                  <Moon className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
              <Separator />
              <div className="space-y-4">
                <Label>Theme Preview</Label>
                <div className="grid gap-4 sm:grid-cols-2">
                  <button
                    type="button"
                    className={`rounded-lg border-2 p-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                      theme === 'light'
                        ? 'border-primary bg-white'
                        : 'border-transparent bg-white/80'
                    }`}
                    onClick={() => theme !== 'light' && handleThemeToggle()}
                    aria-pressed={theme === 'light'}
                    aria-label="Switch to light mode"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Sun className="h-4 w-4" />
                      <span className="font-medium text-black">Light</span>
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 w-full bg-gray-200 rounded"></div>
                      <div className="h-2 w-3/4 bg-gray-200 rounded"></div>
                      <div className="h-2 w-1/2 bg-gray-200 rounded"></div>
                    </div>
                  </button>
                  <button
                    type="button"
                    className={`rounded-lg border-2 p-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                      theme === 'dark'
                        ? 'border-primary bg-gray-900'
                        : 'border-transparent bg-gray-900/80'
                    }`}
                    onClick={() => theme !== 'dark' && handleThemeToggle()}
                    aria-pressed={theme === 'dark'}
                    aria-label="Switch to dark mode"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Moon className="h-4 w-4 text-white" />
                      <span className="font-medium text-white">Dark</span>
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 w-full bg-gray-700 rounded"></div>
                      <div className="h-2 w-3/4 bg-gray-700 rounded"></div>
                      <div className="h-2 w-1/2 bg-gray-700 rounded"></div>
                    </div>
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notification Settings */}
        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-primary" />
                <CardTitle>Notification Preferences</CardTitle>
              </div>
              <CardDescription>
                Choose what notifications you want to receive
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                {notificationOptions.map((option, index) => (
                  <div key={option.key} className="space-y-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="space-y-0.5">
                        <Label>{option.label}</Label>
                        <p className="text-sm text-muted-foreground">
                          {option.description}
                        </p>
                      </div>
                      <Switch
                        className="shrink-0"
                        checked={notifications[option.key]}
                        onCheckedChange={() => handleNotificationChange(option.key)}
                      />
                    </div>
                    {index < notificationOptions.length - 1 && <Separator />}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Admin Settings */}
        {isAdmin && (
          <TabsContent value="admin" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Building className="h-5 w-5 text-primary" />
                  <CardTitle>System Information</CardTitle>
                </div>
                <CardDescription>
                  Application and system configuration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="p-4 rounded-lg border bg-muted/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Globe className="h-4 w-4 text-primary" />
                      <span className="font-medium">API Environment</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {import.meta.env.VITE_API_URL || 'https://awbbackend.bliitz.in'}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg border bg-muted/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Settings className="h-4 w-4 text-primary" />
                      <span className="font-medium">Application Version</span>
                    </div>
                    <p className="text-sm text-muted-foreground">v1.0.0</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  <CardTitle>Quick Admin Actions</CardTitle>
                </div>
                <CardDescription>
                  Access administrative features
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <Button variant="outline" className="h-24 flex flex-col" asChild>
                    <a href="/users">
                      <Users className="h-6 w-6 mb-2" />
                      <span>User Management</span>
                    </a>
                  </Button>
                  <Button variant="outline" className="h-24 flex flex-col" asChild>
                    <a href="/master-data">
                      <Settings className="h-6 w-6 mb-2" />
                      <span>Master Data</span>
                    </a>
                  </Button>
                  <Button variant="outline" className="h-24 flex flex-col" asChild>
                    <a href="/companies">
                      <Building className="h-6 w-6 mb-2" />
                      <span>Company Settings</span>
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  <CardTitle>Session Settings</CardTitle>
                </div>
                <CardDescription>
                  Configure authentication and session behavior
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg border bg-muted/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">Session Timeout</span>
                    <Badge variant="secondary">30 minutes</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Sessions will automatically expire after 30 minutes of inactivity
                  </p>
                </div>
                <div className="p-4 rounded-lg border bg-muted/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">Token Refresh</span>
                    <Badge variant="secondary">Automatic</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Access tokens are automatically refreshed to keep you logged in
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default SettingsPage;
